// scenes/BaseInteriorScene.js
import Phaser from "phaser";

export default class BaseInteriorScene extends Phaser.Scene {
  constructor(
    key,
    collisionData,
    backgroundImage = null,
    doors = {},
    getNpcDialogue = () => null
  ) {
    super({ key });
    this.collisionData = collisionData;
    this.backgroundImage = backgroundImage;
    this.doors = doors;
    this.getNpcDialogue = getNpcDialogue;
  }

  preload() {
    this.load.spritesheet(
      "hero",
      require("../../@shared/assets/GameMap/CharactaerMovementSpritesheet.png"),
      {
        frameWidth: 128,
        frameHeight: 192,
      }
    );

    if (this.backgroundImage) {
      const bgKey = this.scene.key + "_bg"; // 👈 Unique per scene
      this.load.image(bgKey, this.backgroundImage);
    }
  }

  drawCollisionDebugBox(x, y, width, height, color = 0xffffff, alpha = 0.3) {
    const g = this.add.graphics();
    g.lineStyle(1, color, 1);
    g.fillStyle(color, alpha);
    g.strokeRect(x - width / 2, y - height / 2, width, height);
    g.fillRect(x - width / 2, y - height / 2, width, height);
  }

  create(data = {}) {
    const spawn = data.spawn || { x: 300, y: 300 };

    let offsetX = 0;
    let offsetY = 0;

    // Add background for visibility
    if (data.backgroundImage) this.backgroundImage = data.backgroundImage;
    if (data.collisionData) this.collisionData = data.collisionData;
    if (this.backgroundImage) {
      const bgKey = this.scene.key + "_bg";
      const bg = this.add.image(0, 0, bgKey).setOrigin(0);

      const mapWidth = bg.width;
      const mapHeight = bg.height;

      const camWidth = this.cameras.main.width;
      const camHeight = this.cameras.main.height;

      // ✅ This centers the background
      this.mapOffset = {
        x: (camWidth - mapWidth) / 2,
        y: (camHeight - mapHeight) / 2,
      };

      let widthRatio = mapWidth / (camWidth - mapWidth);
      let heightRatio = mapHeight / (camHeight - mapHeight);

      console.log(widthRatio);
      console.log(heightRatio);

      const getOffSetMultiplier = () => {
        let multiplier = { x: 0, y: 0 };
        if (widthRatio > 0.5) {
          multiplier.x = 0.25;
        } else {
          multiplier.x = 0.36;
        }
        if (heightRatio > 0.5) {
          multiplier.y = 0.05;
        } else {
          multiplier.y = 0.36;
        }

        return multiplier;
      };

      const offsetMultiplier = getOffSetMultiplier();
      offsetX = this.mapOffset.x * offsetMultiplier.x;
      offsetY = this.mapOffset.y * offsetMultiplier.y;

      // Move background into position
      bg.setPosition(offsetX, offsetY);

      this.cameras.main.setBounds(0, 0, bg.width, bg.height);
      this.matter.world.setBounds(offsetX, offsetY, bg.width, bg.height);
    } else {
      this.add.rectangle(0, 0, 1000, 1000, 0x111111).setOrigin(0);
    }

    this.npcDialogueText = null;

    // Player setup
    this.player = this.matter.add
      .sprite(spawn.x + 120, spawn.y, "hero", 0)
      .setBody({ width: 64, height: 80 })
      .setFixedRotation();

    // detect mobile browsers via Phaser’s built-in flags
    const isMobile =
      this.sys.game.device.os.android || this.sys.game.device.os.iOS;
    // pick a looser zoom on phone so you see more of the room
    const zoomLevel = isMobile ? 1.2 : 2.0;

    this.cameras.main
      .startFollow(this.player, true, 0.1, 0.1)
      .setZoom(zoomLevel);

    // Animations
    ["down", "left", "right", "up"].forEach((d, i) => {
      this.anims.create({
        key: `walk-${d}`,
        frames: this.anims.generateFrameNumbers("hero", {
          start: 4 * i,
          end: 4 * i + 3,
        }),
        frameRate: 8,
        repeat: -1,
      });
    });

    // Input
    this.joystick = { x: 0, y: 0 };
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys({
      W: Phaser.Input.Keyboard.KeyCodes.W,
      A: Phaser.Input.Keyboard.KeyCodes.A,
      S: Phaser.Input.Keyboard.KeyCodes.S,
      D: Phaser.Input.Keyboard.KeyCodes.D,
    });

    this.spaceKey = this.input.keyboard.addKey(
      Phaser.Input.Keyboard.KeyCodes.SPACE
    );
    this.spaceKey.on("down", () => {
      if (typeof this._onEnter === "function") {
        this._onEnter();
      }
    });

    // Collision setup
    this.collisionData.buildings.forEach((b) => {
      const xs = b.polygon.map((p) => p[0]);
      const ys = b.polygon.map((p) => p[1]);
      const minX = Math.min(...xs),
        maxX = Math.max(...xs);
      const minY = Math.min(...ys),
        maxY = Math.max(...ys);
      const x = minX + (maxX - minX) / 2 + offsetX;
      const y = minY + (maxY - minY) / 2 + offsetY;

      const width = maxX - minX;
      const height = maxY - minY;

      const isNPC =
        b.name &&
        [
          "receptionist",
          "merchant",
          "decksmith",
          "questmaster",
          "questboard1",
          "questboard2",
          "voidcityfrog",
        ].includes(b.name);

      const isExit = b.name === "exit";

      const label = isNPC ? `npc_${b.name}` : isExit ? "exit" : undefined;

      this.matter.add.rectangle(x, y, width, height, {
        isStatic: true,
        isSensor: isNPC || isExit,
        label,
      });

      const color = isNPC ? 0x00ff00 : isExit ? 0x0000ff : 0xff0000;

      //   sathis.drawCollisionDebugBox(x, y, width, height, color, 0.3);
    });

    // Proximity triggers
    this.currentNPC = null;
    this.currentDoor = null;
    this.currentDoorPos = new Phaser.Math.Vector2();

    this.matter.world.on("collisionstart", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (sensor && isPlayer) {
          const label = sensor.label; // ✅ EXTRACT IT HERE

          if (label === "exit") {
            this.currentDoor = label;
            this.currentDoorPos = sensor.position;
            window.dispatchEvent(
              new CustomEvent("interact-proximity", { detail: true })
            );
          } else if (label?.startsWith("npc_")) {
            const npcName = label.replace("npc_", "");
            this.currentNPC = npcName;
            window.dispatchEvent(
              new CustomEvent("npc-proximity", { detail: npcName })
            );
          }
        }
      });
    });

    this.matter.world.on("collisionend", (ev) => {
      ev.pairs.forEach(({ bodyA, bodyB }) => {
        const sensor = bodyA.isSensor ? bodyA : bodyB.isSensor ? bodyB : null;
        const isPlayer =
          bodyA === this.player.body || bodyB === this.player.body;

        if (sensor && isPlayer) {
          const label = sensor.label;
          if (label === "exit") {
            this.currentDoor = null;
            this.currentDoorPos = null;
            window.dispatchEvent(
              new CustomEvent("interact-proximity", { detail: false })
            );
          } else if (label?.startsWith("npc_")) {
            this.currentNPC = null;
            window.dispatchEvent(
              new CustomEvent("npc-proximity", { detail: null })
            );
            if (this.npcDialogueText) {
              this.npcDialogueText.destroy();
              this.npcDialogueText = null;
            }
          }
        }
      });
    });

    this._onEnter = () => {
      if (this.currentNPC) {
        // 🔒 Prevent error: localWorld may not be ready
        if (!this.matter.world?.localWorld?.bodies) return;

        // Fire React UI
        window.dispatchEvent(
          new CustomEvent("npc-dialogue", { detail: this.currentNPC })
        );

        const npcLabel = `npc_${this.currentNPC}`;
        const npcBody = this.matter.world.localWorld.bodies.find(
          (b) => b.label === npcLabel
        );

        const dialogue = this.getNpcDialogue(this.currentNPC);

        if (npcBody && dialogue) {
          const { x, y } = npcBody.position;

          if (this.npcDialogueText) {
            this.npcDialogueText.destroy();
            this.npcDialogueText = null;
          }

          this.npcDialogueText = this.add
            .text(x, y - 60, dialogue.text, {
              font: "14px Arial",
              fill: "#000000",
              backgroundColor: "#ffffff",
              padding: { x: 6, y: 4 },
              wordWrap: { width: 200 },
            })
            .setOrigin(0.5, 1);
        }
      } else if (this.currentDoor && this.doors["exit"]) {
        const target = this.doors["exit"];
        const spawn = target.spawnAt || { x: 300, y: 300 };

        this.scene.stop(this.scene.key);
        this.scene.start(target.to, { spawn });
      }
    };

    // ✅ 2. Add the listener
    window.addEventListener("press-enter", this._onEnter);

    this.events.once("shutdown", () => {
      window.removeEventListener("press-enter", this._onEnter);
      this.spaceKey?.off?.("down"); // 🔥 clean listener
      this.currentNPC = null;
      this.currentDoor = null;
      if (typeof window !== "undefined") {
        window.__npcEngaged = false;
        window.dispatchEvent(
          new CustomEvent("npc-proximity", { detail: null })
        );
        window.dispatchEvent(
          new CustomEvent("interact-proximity", { detail: false })
        );
      }
    });
  }

  update() {
    if (typeof window !== "undefined" && window.__npcEngaged) {
      this.player.setVelocity(0, 0);
      this.player.anims.stop();
      return;
    }

    const speed = 2;
    let vx = 0,
      vy = 0;

    const { x: jx, y: jy } = this.joystick;
    if (jx || jy) {
      vx = jx * speed;
      vy = jy * speed;
    } else {
      if (this.cursors.left.isDown || this.keys.A.isDown) vx = -speed;
      else if (this.cursors.right.isDown || this.keys.D.isDown) vx = speed;
      if (this.cursors.up.isDown || this.keys.W.isDown) vy = -speed;
      else if (this.cursors.down.isDown || this.keys.S.isDown) vy = speed;
    }

    if (vx && vy) {
      vx *= Math.SQRT1_2;
      vy *= Math.SQRT1_2;
    }

    this.player.setVelocity(vx, vy);

    if (vx < 0) this.player.anims.play("walk-left", true);
    else if (vx > 0) this.player.anims.play("walk-right", true);
    else if (vy < 0) this.player.anims.play("walk-up", true);
    else if (vy > 0) this.player.anims.play("walk-down", true);
    else this.player.anims.stop();
  }
}
